<template>
  <ListComponent title="Vendor Activity Monitor" :backRoute="{ name: 'app-admin-vendor-subhire-upload-list' }">
    <template #actions>
      <v-btn color="primary" outlined depressed @click="loadAll">
        <v-icon left small>mdi-refresh</v-icon>
        Refresh
      </v-btn>
    </template>

    <template #list>
      <v-row class="ma-0">
        <!-- stale pending warning -->
        <v-col v-if="overall.stale_pending > 0" cols="12" class="pa-2">
          <v-alert dense outlined type="warning" icon="mdi-alert-outline" class="mb-0 stale-alert" @click="openStaleDrill">
            <span class="font-weight-bold">{{ overall.stale_pending }}</span>
            job(s) have been pending for more than {{ overall.stale_days }} day(s) with no action taken.
            <span class="text-decoration-underline">View jobs</span>
          </v-alert>
        </v-col>

        <!-- overall status cards -->
        <v-col cols="12" class="pa-2">
          <div class="stat-strip">
            <v-card outlined class="fill-height stat-strip-card">
              <v-card-text class="pa-3">
                <div class="d-flex align-center">
                  <v-avatar color="grey lighten-3" size="32" class="mr-2">
                    <v-icon color="grey darken-2">mdi-file-multiple-outline</v-icon>
                  </v-avatar>
                  <span class="overline grey--text">Total Uploads</span>
                </div>
                <div class="text-h5 font-weight-bold mt-2">{{ overall.uploads }}</div>
                <div class="caption grey--text">All sources</div>
              </v-card-text>
            </v-card>

            <v-card outlined class="fill-height stat-strip-card">
              <v-card-text class="pa-3">
                <div class="d-flex align-center">
                  <v-avatar color="grey lighten-3" size="32" class="mr-2">
                    <v-icon color="grey darken-2">mdi-office-building</v-icon>
                  </v-avatar>
                  <span class="overline grey--text">Admin Uploads</span>
                </div>
                <div class="text-h5 font-weight-bold mt-2">
                  {{ sourceSplit.own.uploads }}
                </div>
                <div class="caption grey--text">Own upload</div>
              </v-card-text>
            </v-card>

            <v-card outlined class="fill-height stat-strip-card">
              <v-card-text class="pa-3">
                <div class="d-flex align-center">
                  <v-avatar color="grey lighten-3" size="32" class="mr-2">
                    <v-icon color="grey darken-2">mdi-truck-outline</v-icon>
                  </v-avatar>
                  <span class="overline grey--text">Vendor Uploads</span>
                </div>
                <div class="text-h5 font-weight-bold mt-2">
                  {{ sourceSplit.vendor.uploads }}
                </div>
                <div class="caption grey--text">Vendor self-upload</div>
              </v-card-text>
            </v-card>

            <v-card
              v-for="stat in statusStats"
              :key="stat.key"
              outlined
              class="fill-height stat-strip-card"
            >
              <v-card-text class="pa-3">
                <div class="d-flex align-center">
                  <v-avatar :color="stat.soft" size="32" class="mr-2">
                    <v-icon :color="stat.color">{{ stat.icon }}</v-icon>
                  </v-avatar>
                  <span class="overline grey--text">{{ stat.label }}</span>
                </div>
                <div class="text-h5 font-weight-bold mt-2" :style="{ color: stat.color }">
                  {{ overall[stat.key] }}
                </div>
                <div class="caption grey--text">&nbsp;</div>
              </v-card-text>
            </v-card>

            <v-card outlined class="fill-height stat-strip-card">
              <v-card-text class="pa-3">
                <div class="d-flex align-center">
                  <v-avatar color="teal lighten-5" size="32" class="mr-2">
                    <v-icon color="teal darken-2">mdi-handshake-outline</v-icon>
                  </v-avatar>
                  <span class="overline grey--text">Sub Hires Created</span>
                </div>
                <div class="text-h5 font-weight-bold mt-2 teal--text text--darken-2">
                  {{ overall.subhires_created }}
                </div>
                <div class="caption grey--text">From accepted uploads</div>
              </v-card-text>
            </v-card>
          </div>
        </v-col>

        <!-- day-wise chart -->
        <v-col cols="12" class="pa-2">
          <v-card outlined>
            <v-card-text>
              <div class="d-flex align-center justify-space-between mb-2 flex-wrap" style="gap: 12px">
                <div>
                  <div class="subtitle-1 font-weight-bold">Upload status</div>
                  <div class="caption grey--text">day-wise Approved / Pending / Partial / Rejected</div>
                </div>
                <div class="toggle d-flex align-center">
                  <v-btn
                    v-for="opt in sourceOptions"
                    :key="opt.value"
                    :color="curSource === opt.value ? 'primary' : 'grey lighten-3'"
                    :dark="curSource === opt.value"
                    small
                    depressed
                    class="mx-1"
                    @click="onSourceChange(opt.value)"
                  >
                    {{ opt.text }}
                  </v-btn>
                  <v-btn
                    outlined
                    depressed
                    small
                    color="primary"
                    class="ml-2"
                    :disabled="!days.length"
                    @click="exportChart"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    Export
                  </v-btn>
                </div>
              </div>

              <div class="d-flex align-center flex-wrap mb-3" style="gap: 12px">
                <span class="caption grey--text text-uppercase">From</span>
                <DateField
                  :inputProps="{ dense: true, hideDetails: 'auto', style: 'max-width: 160px' }"
                  v-model="dateRange.from"
                  @input="onDateRangeChange"
                />
                <span class="caption grey--text text-uppercase">To</span>
                <DateField
                  :inputProps="{ dense: true, hideDetails: 'auto', style: 'max-width: 160px' }"
                  v-model="dateRange.to"
                  @input="onDateRangeChange"
                />
                <v-btn v-if="!isDateRangeToday" text small color="primary" @click="clearDateRange">
                  Reset to today
                </v-btn>
              </div>

              <div v-if="chartLabels.length" class="chart-box">
                <Chart
                  :key="curSource"
                  :labels="chartLabels"
                  :datasets="chartDatasets"
                  :options="chartOptions"
                />
              </div>
              <div v-else class="text-center grey--text py-8">No upload activity in this period.</div>

              <div class="legend caption grey--text mt-3">
                <span><span class="swatch" style="background: #4CAF7D"></span>Approved</span>
                <span><span class="swatch" style="background: #f07025"></span>Pending</span>
                <span><span class="swatch" style="background: #5C6BC0"></span>Partial</span>
                <span><span class="swatch" style="background: #9E9E9E"></span>Rejected</span>
                <span><span class="swatch" style="background: #00897B"></span>Sub Hires Created (line)</span>
              </div>
              <div class="cstat caption grey--text mt-2 pt-2">
                <span>Containers &mdash;</span>
                <span>
                  <span class="swatch" style="background: #4CAF7D"></span>
                  Approved <b class="success--text">{{ containerStats.approved }}</b>
                </span>
                <span>
                  <span class="swatch" style="background: #f07025"></span>
                  Remaining (pending) <b class="warning--text">{{ containerStats.remaining }}</b>
                </span>
                <span>of {{ overall.containers }} total</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- day-wise table -->
        <v-col cols="12" class="pa-2">
          <v-card outlined>
            <v-card-text>
              <div class="subtitle-1 font-weight-bold mb-1">Day-wise summary</div>
              <div class="caption grey--text mb-3">click a status count to drill in</div>

              <div class="d-flex align-center flex-wrap mb-3" style="gap: 12px">
                <span class="caption grey--text text-uppercase">From</span>
                <DateField
                  :inputProps="{ dense: true, hideDetails: 'auto', style: 'max-width: 160px' }"
                  v-model="tableDateRange.from"
                  @input="onTableDateRangeChange"
                />
                <span class="caption grey--text text-uppercase">To</span>
                <DateField
                  :inputProps="{ dense: true, hideDetails: 'auto', style: 'max-width: 160px' }"
                  v-model="tableDateRange.to"
                  @input="onTableDateRangeChange"
                />
                <v-btn v-if="!isTableDateRangeToday" text small color="primary" @click="clearTableDateRange">
                  Reset to today
                </v-btn>
              </div>

              <div class="recon">
                <div class="th">
                  <div>Day</div>
                  <div class="r">Jobs</div>
                  <div class="r">Containers</div>
                  <div class="r">Approved</div>
                  <div class="r">Pending</div>
                  <div class="r">Partial</div>
                  <div class="r">Rejected</div>
                </div>
                <div v-for="row in tableDays" :key="row.day" class="tr">
                  <div>{{ formatDay(row.day) }}</div>
                  <div class="r mono">{{ row.jobs }}</div>
                  <div class="r mono teal--text text--darken-1">{{ row.containers }}</div>
                  <div class="r mono success--text">
                    {{ row.approved }}
                  </div>
                  <div class="r">
                    <button
                      v-if="row.pending > 0"
                      type="button"
                      class="pchip pchip--warning"
                      @click="openDrill(row.day, 'pending')"
                    >
                      {{ row.pending }}
                      <v-icon x-small color="warning">mdi-menu-right</v-icon>
                    </button>
                    <span v-else class="grey--text mono">0</span>
                  </div>
                  <div class="r">
                    <button
                      v-if="row.partial > 0"
                      type="button"
                      class="pchip pchip--info"
                      @click="openDrill(row.day, 'partial')"
                    >
                      {{ row.partial }}
                      <v-icon x-small color="info">mdi-menu-right</v-icon>
                    </button>
                    <span v-else class="grey--text mono">0</span>
                  </div>
                  <div class="r">
                    <button
                      v-if="row.rejected > 0"
                      type="button"
                      class="pchip pchip--error"
                      @click="openDrill(row.day, 'rejected')"
                    >
                      {{ row.rejected }}
                      <v-icon x-small color="error">mdi-menu-right</v-icon>
                    </button>
                    <span v-else class="grey--text mono">0</span>
                  </div>
                </div>
                <div v-if="!tableDays.length" class="text-center grey--text py-6">No data.</div>
                <div class="tot" v-if="tableDays.length">
                  <div>Total</div>
                  <div class="r mono">{{ tableTotals.jobs }}</div>
                  <div class="r mono teal--text text--darken-1">{{ tableTotals.containers }}</div>
                  <div class="r mono success--text">{{ tableTotals.approved }}</div>
                  <div class="r mono warning--text">{{ tableTotals.pending }}</div>
                  <div class="r mono info--text">{{ tableTotals.partial }}</div>
                  <div class="r mono error--text">{{ tableTotals.rejected }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- vendors needing attention -->
        <v-col cols="12" class="pa-2">
          <v-card outlined>
            <v-card-text>
              <div class="subtitle-1 font-weight-bold mb-1">Vendors needing attention</div>
              <div class="caption grey--text mb-3">
                sorted by pending + rejected jobs &middot; click a vendor to view their jobs
              </div>
              <div v-if="!vendorLeaderboard.length" class="text-center grey--text py-6">
                No vendor currently has pending or rejected jobs.
              </div>
              <div v-else class="recon vendor-board">
                <div class="th">
                  <div>Vendor</div>
                  <div class="r">Jobs</div>
                  <div class="r">Approved</div>
                  <div class="r">Pending</div>
                  <div class="r">Partial</div>
                  <div class="r">Rejected</div>
                </div>
                <button
                  v-for="row in vendorLeaderboard"
                  :key="row.vendor_id"
                  type="button"
                  class="tr vendor-row"
                  @click="openVendorDrill(row.vendor_id, row.vendor_name)"
                >
                  <div class="text-left font-weight-medium">{{ row.vendor_name }}</div>
                  <div class="r mono">{{ row.jobs }}</div>
                  <div class="r mono success--text">{{ row.approved }}</div>
                  <div class="r mono" style="color: #f07025">{{ row.pending }}</div>
                  <div class="r mono info--text">{{ row.partial }}</div>
                  <div class="r mono error--text">{{ row.rejected }}</div>
                </button>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <template #dialogs>
      <v-dialog v-model="drillDialog" scrollable max-width="900">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>{{ drillTitle }}</span>
            <v-btn icon @click="drillDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-card-text class="pt-4" style="max-height: 60vh">
            <v-progress-linear v-if="drillLoading" indeterminate color="primary" class="mb-3"></v-progress-linear>
            <div v-if="!drillLoading && drillJobs.length === 0" class="text-center grey--text py-8">
              No jobs found.
            </div>
            <v-card v-for="job in drillJobs" :key="job.id" outlined class="mb-3">
              <v-card-text>
                <div class="d-flex flex-wrap align-center" style="gap: 8px">
                  <span class="font-weight-bold">{{ job.route_title || job.lane_title }}</span>
                  <v-chip small :color="statusColor(job.status)" text-color="white">
                    {{ job.status }}
                  </v-chip>
                  <v-chip small outlined :color="job.source === 'admin' ? 'teal' : 'purple'">
                    {{ job.source === 'admin' ? 'Own upload' : 'Vendor self-upload' }}
                  </v-chip>
                  <span class="caption grey--text ml-auto">{{ job.vendor_name }}</span>
                </div>
                <div class="caption grey--text mt-1">
                  {{ job.container_type_name }} &middot; {{ job.total_containers }} container(s)
                  <router-link
                    class="ml-2"
                    :to="{ name: 'app-admin-vendor-subhire-upload-details', params: { id: job.upload_id } }"
                  >
                    View upload
                  </router-link>
                </div>
                <div class="d-flex flex-wrap mt-2" style="gap: 6px">
                  <v-chip
                    v-for="item in job.items"
                    :key="item.id"
                    x-small
                    :color="item.status === 'accepted' ? 'success' : item.status === 'rejected' ? 'error' : 'warning'"
                    text-color="white"
                  >
                    {{ item.container_number }}
                  </v-chip>
                </div>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-dialog>
    </template>
  </ListComponent>
</template>

<script>
import ListComponent from "@/components/General/ListComponent.vue";
import Chart from "@/components/General/Chart.vue";
import DateField from "@/components/FormBaseComponents/DateField.vue";
import * as XLSX from "xlsx";

export default {
  components: { ListComponent, Chart, DateField },

  data() {
    return {
      loading: false,
      curSource: "all",
      dateRange: {
        from: new Date().toISOString().slice(0, 10),
        to: new Date().toISOString().slice(0, 10),
      },
      sourceOptions: [
        { text: "All", value: "all" },
        { text: "Own", value: "own" },
        { text: "Vendor", value: "vendor" },
      ],
      days: [],
      tableDateRange: {
        from: new Date().toISOString().slice(0, 10),
        to: new Date().toISOString().slice(0, 10),
      },
      tableDays: [],
      overall: {
        uploads: 0,
        jobs: 0,
        containers: 0,
        approved: 0,
        pending: 0,
        partial: 0,
        rejected: 0,
        containers_approved: 0,
        containers_pending: 0,
        stale_pending: 0,
        stale_days: 3,
        subhires_created: 0,
      },
      vendorLeaderboard: [],
      sourceSplit: {
        own: { uploads: 0, containers: 0 },
        vendor: { uploads: 0, containers: 0 },
      },
      statusStats: [
        {
          key: "approved",
          status: "full",
          label: "Approved",
          color: "#166534",
          soft: "green lighten-5",
          icon: "mdi-check-circle-outline",
        },
        {
          key: "pending",
          status: "pending",
          label: "Pending",
          color: "#92400E",
          soft: "amber lighten-5",
          icon: "mdi-clock-outline",
        },
        {
          key: "partial",
          status: "partial",
          label: "Partial",
          color: "#1E40AF",
          soft: "blue lighten-5",
          icon: "mdi-progress-check",
        },
        {
          key: "rejected",
          status: "rejected",
          label: "Rejected",
          color: "#991B1B",
          soft: "red lighten-5",
          icon: "mdi-close-circle-outline",
        },
      ],
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [{ stacked: true, barPercentage: 0.5, categoryPercentage: 0.6 }],
          yAxes: [
            { id: "status", stacked: true, ticks: { beginAtZero: true } },
            {
              id: "subhires",
              position: "right",
              stacked: false,
              gridLines: { drawOnChartArea: false },
              ticks: { beginAtZero: true, precision: 0 },
              scaleLabel: { display: true, labelString: "Sub Hires Created" },
            },
          ],
        },
        legend: { display: false },
      },
      drillDialog: false,
      drillLoading: false,
      drillJobs: [],
      drillTitle: "",
    };
  },

  computed: {
    isDateRangeToday() {
      const today = new Date().toISOString().slice(0, 10);
      return this.dateRange.from === today && this.dateRange.to === today;
    },
    isTableDateRangeToday() {
      const today = new Date().toISOString().slice(0, 10);
      return this.tableDateRange.from === today && this.tableDateRange.to === today;
    },
    chartLabels() {
      return this.days.map((d) => this.formatDay(d.day));
    },
    chartDatasets() {
      return [
        { label: "Approved", yAxisID: "status", backgroundColor: "#4CAF7D", data: this.days.map((d) => d.approved) },
        { label: "Pending", yAxisID: "status", backgroundColor: "#f07025", data: this.days.map((d) => d.pending) },
        { label: "Partial", yAxisID: "status", backgroundColor: "#5C6BC0", data: this.days.map((d) => d.partial) },
        { label: "Rejected", yAxisID: "status", backgroundColor: "#9E9E9E", data: this.days.map((d) => d.rejected) },
        {
          type: "line",
          label: "Sub Hires Created",
          yAxisID: "subhires",
          borderColor: "#00897B",
          backgroundColor: "#00897B",
          fill: false,
          lineTension: 0,
          pointRadius: 3,
          data: this.days.map((d) => d.subhires_created || 0),
        },
      ];
    },
    containerStats() {
      return {
        approved: this.overall.containers_approved || 0,
        remaining: this.overall.containers_pending || 0,
      };
    },
    tableTotals() {
      const totals = { jobs: 0, containers: 0, approved: 0, pending: 0, partial: 0, rejected: 0 };
      this.tableDays.forEach((row) => {
        totals.jobs += row.jobs;
        totals.containers += row.containers;
        totals.approved += row.approved;
        totals.pending += row.pending;
        totals.partial += row.partial;
        totals.rejected += row.rejected;
      });
      return totals;
    },
  },

  methods: {
    formatDay(day) {
      if (!day) return "";
      return this.$globals.formatDate(day);
    },
    statusColor(status) {
      const map = { full: "success", pending: "warning", partial: "info", rejected: "error" };
      return map[status] || "grey";
    },
    onSourceChange(value) {
      this.curSource = value;
      this.loadStats();
    },
    onDateRangeChange() {
      this.loadStats();
    },
    clearDateRange() {
      const today = new Date().toISOString().slice(0, 10);
      this.dateRange = { from: today, to: today };
      this.loadStats();
    },
    loadStats() {
      this.loading = true;
      this.$bus.$emit("showLoader", true);
      const params = {};
      if (this.curSource !== "all") {
        params.source = this.curSource;
      }
      if (this.dateRange.from) {
        params.from_date = this.dateRange.from;
      }
      if (this.dateRange.to) {
        params.to_date = this.dateRange.to;
      }
      this.$api.vendorSubHireUpload
        .getUploadMonitorStats(params)
        .then((res) => {
          this.days = res.data.days || [];
          this.overall = res.data.overall || this.overall;
          this.sourceSplit = res.data.source_split || this.sourceSplit;
          this.vendorLeaderboard = res.data.vendor_leaderboard || [];
        })
        .catch(() => {
          this.$bus.$emit("showToastMessage", {
            message: "Could not load activity data",
            color: "error",
          });
        })
        .finally(() => {
          this.loading = false;
          this.$bus.$emit("showLoader", false);
        });
    },
    exportChart() {
      if (!this.days.length) return;
      const sourceLabel =
        (this.sourceOptions.find((o) => o.value === this.curSource) || {}).text ||
        this.curSource;

      const sum = (key) => this.days.reduce((acc, d) => acc + (d[key] || 0), 0);

      const rows = this.days.map((d) => ({
        Day: this.formatDay(d.day),
        Jobs: d.jobs,
        Containers: d.containers,
        Approved: d.approved,
        Pending: d.pending,
        Partial: d.partial,
        Rejected: d.rejected,
        "Sub Hires Created": d.subhires_created || 0,
      }));
      rows.push({
        Day: "Total",
        Jobs: sum("jobs"),
        Containers: sum("containers"),
        Approved: sum("approved"),
        Pending: sum("pending"),
        Partial: sum("partial"),
        Rejected: sum("rejected"),
        "Sub Hires Created": sum("subhires_created"),
      });

      const worksheet = XLSX.utils.json_to_sheet(rows, {
        header: [
          "Day",
          "Jobs",
          "Containers",
          "Approved",
          "Pending",
          "Partial",
          "Rejected",
          "Sub Hires Created",
        ],
        origin: "A4",
      });
      XLSX.utils.sheet_add_aoa(
        worksheet,
        [
          ["Source", sourceLabel],
          ["From", this.dateRange.from],
          ["To", this.dateRange.to],
        ],
        { origin: "A1" }
      );

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Upload status");
      XLSX.writeFile(
        workbook,
        `upload_status_${this.curSource}_${this.dateRange.from}_${this.dateRange.to}.xlsx`.replace(
          /\//g,
          "-"
        )
      );
    },
    runDrill(title, params) {
      this.drillDialog = true;
      this.drillLoading = true;
      this.drillJobs = [];
      this.drillTitle = title;
      this.$api.vendorSubHireUpload
        .getUploadMonitorJobs(params)
        .then((res) => {
          this.drillJobs = res.data || [];
        })
        .catch(() => {
          this.$bus.$emit("showToastMessage", {
            message: "Could not load jobs",
            color: "error",
          });
        })
        .finally(() => {
          this.drillLoading = false;
        });
    },
    openDrill(day, status) {
      const labels = { full: "Approved", pending: "Pending", partial: "Partial", rejected: "Rejected" };
      const title = day
        ? `${labels[status] || status} jobs — ${this.formatDay(day)}`
        : `${labels[status] || status} jobs — all days`;
      const params = { status };
      if (day) {
        params.day = day;
      }
      if (this.curSource !== "all") {
        params.source = this.curSource;
      }
      this.runDrill(title, params);
    },
    openVendorDrill(vendorId, vendorName) {
      this.runDrill(`Pending jobs — ${vendorName}`, { vendor: vendorId, status: "pending" });
    },
    openStaleDrill() {
      this.runDrill(
        `Pending for more than ${this.overall.stale_days} day(s)`,
        { stale_days: this.overall.stale_days }
      );
    },
    onTableDateRangeChange() {
      this.loadTableDays();
    },
    clearTableDateRange() {
      const today = new Date().toISOString().slice(0, 10);
      this.tableDateRange = { from: today, to: today };
      this.loadTableDays();
    },
    loadTableDays() {
      const params = {};
      if (this.tableDateRange.from) {
        params.from_date = this.tableDateRange.from;
      }
      if (this.tableDateRange.to) {
        params.to_date = this.tableDateRange.to;
      }
      this.$api.vendorSubHireUpload
        .getUploadMonitorStats(params)
        .then((res) => {
          this.tableDays = res.data.days || [];
        })
        .catch(() => {
          this.$bus.$emit("showToastMessage", {
            message: "Could not load day-wise summary",
            color: "error",
          });
        });
    },
    loadAll() {
      this.loadStats();
      this.loadTableDays();
    },
  },

  mounted() {
    this.loadAll();
  },
};
</script>

<style scoped>
.chart-box {
  position: relative;
  height: 180px;
  width: 100%;
  overflow: hidden;
}
.chart-box >>> canvas {
  max-height: 180px !important;
}
.legend {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
}
.legend span,
.cstat span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.cstat {
  border-top: 1px solid #e2e8e9;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}
.cstat b {
  font-variant-numeric: tabular-nums;
}
.swatch {
  width: 9px;
  height: 9px;
  border-radius: 3px;
  display: inline-block;
}
.stat-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.stat-strip-card {
  flex: 1 1 150px;
}
.stat-strip-card >>> .v-card__text {
  min-height: 96px;
}
.stat-card {
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}
.stat-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-2px);
}
.pchip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: none;
  border-radius: 99px;
  padding: 2px 6px 2px 10px;
  font-family: inherit;
  font-weight: 600;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition: filter 0.15s ease;
}
.pchip:hover {
  filter: brightness(0.95);
}
.pchip--success {
  color: #166534;
  background: #dcfce7;
}
.pchip--warning {
  color: #92400e;
  background: #fef3c7;
}
.pchip--info {
  color: #1e40af;
  background: #dbeafe;
}
.pchip--error {
  color: #991b1b;
  background: #fee2e2;
}
.recon {
  border: 1px solid #e2e8e9;
  border-radius: 10px;
  overflow: hidden;
}
.recon .th,
.recon .tr,
.recon .tot {
  display: grid;
  grid-template-columns: 1.2fr 0.7fr 0.9fr 0.8fr 0.8fr 0.8fr 0.8fr;
  gap: 8px;
  padding: 9px 12px;
  align-items: center;
}
.recon .th {
  background: #f3f6f6;
  color: #8a9ba1;
  font-size: 12px;
  font-weight: 600;
  border-bottom: 1px solid #e2e8e9;
}
.recon .tr {
  font-size: 14px;
  border-bottom: 1px solid #e2e8e9;
}
.stale-alert {
  cursor: pointer;
}
.vendor-board .th,
.vendor-board .tr {
  grid-template-columns: 1.6fr 0.6fr 0.7fr 0.7fr 0.7fr 0.7fr;
}
.vendor-row {
  width: 100%;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}
.vendor-row:hover {
  background-color: rgba(0, 0, 0, 0.03);
}
.recon .tr:last-of-type {
  border-bottom: none;
}
.recon .tot {
  background: #f3f6f6;
  font-weight: 700;
  font-size: 14px;
}
.r {
  text-align: right;
}
.mono {
  font-variant-numeric: tabular-nums;
}
</style>
